import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatePickerComponent } from '@components/date-picker';
import { FormsModule } from '@angular/forms';
import { InputComponent } from '@components/input';
import { MsfSelectPaginated } from '@components/select/paginated/select-paginated';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { VarietyService } from '@6grain/masofa-monolith-api-client/dictionaries/api/variety.service';
import { FilterOperator } from '@app/utils/filter-operator';

export interface SeasonPeriodRow {
  name: string;
  type: 'select-crop' | 'select-variety' | 'date' | 'number';
  modelField1: string;
  modelField2: string;
  dependsOn1?: string;
  dependsOn2?: string;
}

@Component({
  selector: 'app-season-period-edit',
  standalone: true,
  templateUrl: './season-period-edit.html',
  styleUrl: './season-period-edit.scss',
  imports: [DataTableComponent, DatePickerComponent, TranslatePipe, FormsModule, InputComponent, MsfSelectPaginated],
})
export class SeasonPeriodEdit implements AfterViewInit, OnInit {
  public tableColumns: TableColumn[] = [];
  plannedVarietyFilter: Array<{ filterField: string; filterOperator: FilterOperator; filterValue: any }> = [];
  actualVarietyFilter: Array<{ filterField: string; filterOperator: FilterOperator; filterValue: any }> = [];

  @ViewChild('PlannedTemplate') plannedTemplate: any;
  @ViewChild('FactTemplate') factTemplate: any;

  @Input() model: any = {};
  /** true — only plan column (read-only); false — only fact column (editable). */
  @Input() isPlanned = false;
  @Input() canSave: boolean = true;
  @Output() modelChange = new EventEmitter<any>();
  @Output() canSaveChange = new EventEmitter<boolean>();
  @Output() validationChange = new EventEmitter<boolean>();

  public harvestDateError = signal<string | null>(null);
  public harvestDatePlanError = signal<string | null>(null);

  tableConfig: TableConfig = {
    selectable: false,
    paginator: false,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50],
    loading: false,
    emptyMessage: 'No data found',
    keepOrder: true,
  };

  translateService = inject(TranslateService);
  cropService = inject(CropService);
  varietyService = inject(VarietyService);

  data: SeasonPeriodRow[] = [];

  ngOnInit() {
    this.updateVarietyFilters();

    this.data = [
      {
        name: this.translateService.instant('Crop'),
        type: 'select-crop',
        modelField1: 'plannedCropId',
        modelField2: 'cropId',
      },
      {
        name: this.translateService.instant('Variety'),
        type: 'select-variety',
        modelField1: 'plannedVarietyId',
        modelField2: 'varietyId',
        dependsOn1: 'plannedCropId',
        dependsOn2: 'cropId',
      },
      {
        name: this.translateService.instant('Planting date'),
        type: 'date',
        modelField1: 'plantingDatePlan',
        modelField2: 'plantingDate',
      },
      {
        name: this.translateService.instant('Harvesting date'),
        type: 'date',
        modelField1: 'harvestingDatePlan',
        modelField2: 'harvestingDate',
      },
      {
        name: this.translateService.instant('Harvest, t'),
        type: 'number',
        modelField1: 'yieldPlan',
        modelField2: 'yieldFact',
      },
      {
        name: this.translateService.instant('Yield, t'),
        type: 'number',
        modelField1: 'yieldHaPlan',
        modelField2: 'yieldHaFact',
      },
    ];
  }

  ngAfterViewInit(): void {
    this.tableColumns = [
      {
        field: 'name',
        header: 'Name',
        type: 'text',
        width: '40%',
      },
      this.isPlanned
        ? {
            field: 'planned',
            header: 'Planned',
            type: 'custom',
            customTemplate: this.plannedTemplate,
            width: '60%',
          }
        : {
            field: 'fact',
            header: 'Actual',
            type: 'custom',
            customTemplate: this.factTemplate,
            width: '60%',
          },
    ];
  }

  validateDates(): void {
    if (this.isPlanned) {
      this.validatePlanDates();
      this.harvestDateError.set(null);
    } else {
      this.validateFactDates();
      this.harvestDatePlanError.set(null);
    }
    this.emitValidation();
  }

  validateFactDates(): void {
    const plantingDate = this.model?.plantingDate;
    const harvestingDate = this.model?.harvestingDate;

    if (plantingDate && harvestingDate) {
      const planting = new Date(plantingDate);
      const harvesting = new Date(harvestingDate);

      if (harvesting < planting) {
        this.harvestDateError.set(this.translateService.instant('Harvest date cannot be earlier than planting date'));
      } else {
        this.harvestDateError.set(null);
      }
    } else {
      this.harvestDateError.set(null);
    }
  }

  validatePlanDates(): void {
    const plantingDatePlan = this.model?.plantingDatePlan;
    const harvestingDatePlan = this.model?.harvestingDatePlan;

    if (plantingDatePlan && harvestingDatePlan) {
      const planting = new Date(plantingDatePlan);
      const harvesting = new Date(harvestingDatePlan);

      if (harvesting < planting) {
        this.harvestDatePlanError.set(
          this.translateService.instant('Planned harvest date cannot be earlier than planned planting date'),
        );
      } else {
        this.harvestDatePlanError.set(null);
      }
    } else {
      this.harvestDatePlanError.set(null);
    }
  }

  emitValidation(): void {
    const isValid = !this.harvestDateError() && !this.harvestDatePlanError();
    this.canSave = isValid;
    this.canSaveChange.emit(isValid);
    this.validationChange.emit(isValid);
  }

  isValid(): boolean {
    return !this.harvestDateError() && !this.harvestDatePlanError();
  }

  onDateChange(): void {
    this.validateDates();
    this.modelChange.emit(this.model);
  }

  onFieldChange(row: any, side: 'planned' | 'actual'): void {
    if (this.isPlanned) {
      return;
    }

    if (row?.type === 'select-crop') {
      if (side === 'planned') {
        this.model.plannedVarietyId = null;
      } else {
        this.model.varietyId = null;
      }
      this.updateVarietyFilters(side);
    }

    this.validateDates();
    this.modelChange.emit(this.model);
  }

  private updateVarietyFilters(side?: 'planned' | 'actual'): void {
    if (side === 'planned') {
      this.plannedVarietyFilter = this.buildVarietyFilter(this.model?.plannedCropId);
      return;
    }
    if (side === 'actual') {
      this.actualVarietyFilter = this.buildVarietyFilter(this.model?.cropId);
      return;
    }

    this.plannedVarietyFilter = this.buildVarietyFilter(this.model?.plannedCropId);
    this.actualVarietyFilter = this.buildVarietyFilter(this.model?.cropId);
  }

  private buildVarietyFilter(cropId: string | null | undefined) {
    if (!cropId) {
      return [];
    }

    return [
      {
        filterField: 'cropId',
        filterOperator: FilterOperator.Equals,
        filterValue: cropId,
      },
    ];
  }
}
